import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Undo2,
  Clock as ClockIcon, Users, Crown, Sparkles, Info, HelpCircle, MailCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── small helpers ────────────────────────────────────────────────── */
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

const listToLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const linesToList = (txt) => (txt || '').split('\n').map((s) => s.trim()).filter(Boolean);

/**
 * AdminEventContentManager
 *
 * Full editor for the /event page. Ships with four sub-sections:
 *   • Timeline  — CRUD + reorder
 *   • Party     — text fields + multi-name lists
 *   • Attire    — 4 cards, each with add/remove key-value rows
 *   • Vendors   — CRUD + reorder
 *
 * Everything saves through a single PUT that partially updates the
 * event-content singleton so the public /event page reflects changes
 * without a redeploy.
 */
const AdminEventContentManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('timeline');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/event/content`);
      const data = res.data || {};
      setInitial(clone(data));
      setContent(clone(data));
    } catch (e) {
      console.error(e);
      setError('Could not load current content.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => (
    content && initial && JSON.stringify(content) !== JSON.stringify(initial)
  ), [content, initial]);

  const save = async () => {
    if (!content || !dirty) return;
    setSaving(true);
    setError('');
    try {
      const res = await axios.put(`${API}/admin/event/content`, content, {
        headers: authHeader(),
      });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (initial) setContent(clone(initial));
  };

  const resetToDefaults = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API}/admin/event/content/reset`, {}, { headers: authHeader() });
      setInitial(clone(res.data));
      setContent(clone(res.data));
      setResetOpen(false);
    } catch (e) {
      console.error(e);
      setError('Failed to reset content.');
    } finally {
      setResetting(false);
    }
  };

  const patch = (fn) => setContent((prev) => {
    const next = clone(prev);
    fn(next);
    return next;
  });

  if (loading || !content) {
    return (
      <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading event content…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-event-content">
      {/* Header — sub-nav + save bar */}
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'timeline', label: 'Timeline', Icon: ClockIcon },
              { id: 'party',    label: 'Wedding Party', Icon: Users },
              { id: 'attire',   label: 'Attire',   Icon: Crown },
              { id: 'vendors',  label: 'Vendors',  Icon: Sparkles },
              { id: 'guest',    label: 'Guest Info', Icon: Info },
              { id: 'rsvp',     label: 'RSVP Deadline', Icon: MailCheck },
              { id: 'faq',      label: 'FAQ', Icon: HelpCircle },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors ${
                  tab === id
                    ? 'bg-[#3B2F2F] text-white'
                    : 'bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]'
                }`}
                data-testid={`event-tab-${id}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setResetOpen(true)}
              className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1"
              title="Restore default content"
              data-testid="event-reset-btn"
            >
              <RotateCcw className="w-3 h-3" /> Reset defaults
            </button>
            {dirty && (
              <button
                onClick={revert}
                disabled={saving}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
                data-testid="event-revert-btn"
              >
                <Undo2 className="w-3.5 h-3.5" /> Discard
              </button>
            )}
            <Button
              onClick={save}
              disabled={!dirty || saving}
              className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60"
              data-testid="event-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>
        {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
      </div>

      {tab === 'timeline' && <TimelineEditor content={content} patch={patch} />}
      {tab === 'party'    && <PartyEditor content={content} patch={patch} />}
      {tab === 'attire'   && <AttireEditor content={content} patch={patch} />}
      {tab === 'vendors'  && <VendorsEditor content={content} patch={patch} />}
      {tab === 'guest'    && <GuestInfoEditor content={content} patch={patch} />}
      {tab === 'rsvp'     && <RsvpDeadlineEditor content={content} patch={patch} />}
      {tab === 'faq'      && <FaqEditor content={content} patch={patch} />}

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset to default content?</DialogTitle>
            <DialogDescription>
              This restores the entire event page to the original placeholder text
              (all your edits will be lost). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetToDefaults} disabled={resetting} data-testid="event-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Reset content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ── Timeline editor ──────────────────────────────────────────────── */
const TimelineEditor = ({ content, patch }) => {
  const items = content.timeline || [];

  const update = (i, key, value) => patch((d) => { d.timeline[i][key] = value; });
  const move = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.timeline.length) return;
    [d.timeline[i], d.timeline[j]] = [d.timeline[j], d.timeline[i]];
  });
  const remove = (i) => patch((d) => { d.timeline.splice(i, 1); });
  const add = () => patch((d) => {
    d.timeline = d.timeline || [];
    d.timeline.push({ time: '', title: 'New Event', description: '' });
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6" data-testid="event-timeline-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#3B2F2F]">Wedding Day Timeline</h3>
          <p className="text-xs text-[#8A7A7A]">Add, remove, or reorder the moments guests will see.</p>
        </div>
        <Button onClick={add} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="timeline-add">
          <Plus className="w-4 h-4 mr-1" /> Add event
        </Button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-[#8A7A7A] italic">No timeline items yet — add one above.</p>
      )}

      <ul className="space-y-3">
        {items.map((row, i) => (
          <li key={i} className="grid grid-cols-1 md:grid-cols-[110px_1fr_auto] gap-3 items-start p-3 rounded-lg border border-[#E5E0D8]" data-testid={`timeline-row-${i}`}>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Time</label>
              <Input
                value={row.time || ''}
                onChange={(e) => update(i, 'time', e.target.value)}
                placeholder="e.g. 3:00 PM"
                data-testid={`timeline-time-${i}`}
              />
            </div>
            <div>
              <div className="mb-2">
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
                <Input
                  value={row.title || ''}
                  onChange={(e) => update(i, 'title', e.target.value)}
                  placeholder="Ceremony"
                  data-testid={`timeline-title-${i}`}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Description</label>
                <Textarea
                  rows={2}
                  value={row.description || ''}
                  onChange={(e) => update(i, 'description', e.target.value)}
                  placeholder="The exchange of vows."
                  data-testid={`timeline-desc-${i}`}
                />
              </div>
            </div>
            <div className="flex md:flex-col items-center gap-1 md:pt-6">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move up"
                data-testid={`timeline-up-${i}`}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move down"
                data-testid={`timeline-down-${i}`}
              >
                <ArrowDown className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(i)}
                className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                title="Delete"
                data-testid={`timeline-delete-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ── Party editor ─────────────────────────────────────────────────── */
const PartyEditor = ({ content, patch }) => {
  const party = content.party || {};

  const setName = (path, value) => patch((d) => {
    if (!d.party[path]) d.party[path] = { name: '' };
    d.party[path].name = value;
  });
  const setList = (key, text) => patch((d) => { d.party[key] = linesToList(text); });

  const single = [
    { key: 'officiant', label: 'Officiant' },
    { key: 'bestMan', label: 'Best Man' },
    { key: 'matronOfHonor', label: 'Maid / Matron of Honor' },
  ];
  const lists = [
    { key: 'bridesmaids',    label: 'Bridesmaids' },
    { key: 'groomsmen',      label: 'Groomsmen' },
    { key: 'flowerGirls',    label: 'Flower Girls' },
    { key: 'ringBearers',    label: 'Ring Bearers' },
    { key: 'parentsOfBride', label: 'Parents of the Bride' },
    { key: 'parentsOfGroom', label: 'Parents of the Groom' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6" data-testid="event-party-editor">
      <div className="mb-4">
        <h3 className="font-serif text-xl text-[#3B2F2F]">Wedding Party</h3>
        <p className="text-xs text-[#8A7A7A]">For list roles, put one name per line.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {single.map(({ key, label }) => (
          <div key={key}>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">{label}</label>
            <Input
              value={party[key]?.name || ''}
              onChange={(e) => setName(key, e.target.value)}
              data-testid={`party-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {lists.map(({ key, label }) => (
          <div key={key}>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">{label}</label>
            <Textarea
              rows={Math.max(3, (party[key] || []).length + 1)}
              value={listToLines(party[key])}
              onChange={(e) => setList(key, e.target.value)}
              placeholder="One name per line"
              data-testid={`party-${key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Attire editor ────────────────────────────────────────────────── */
const AttireEditor = ({ content, patch }) => {
  const attire = content.attire || {};
  // ordered keys to keep card order deterministic
  const orderedKeys = Object.keys(attire);

  const updateField = (piece, key, value) => patch((d) => {
    d.attire[piece][key] = value;
  });
  const renameField = (piece, oldKey, newKey) => patch((d) => {
    if (!newKey || oldKey === newKey || d.attire[piece][newKey] !== undefined) return;
    const val = d.attire[piece][oldKey];
    // Rebuild preserving order
    const rebuilt = {};
    Object.entries(d.attire[piece]).forEach(([k, v]) => {
      rebuilt[k === oldKey ? newKey : k] = k === oldKey ? val : v;
    });
    d.attire[piece] = rebuilt;
  });
  const removeField = (piece, key) => patch((d) => {
    if (key === 'label') return;
    delete d.attire[piece][key];
  });
  const addField = (piece) => patch((d) => {
    let name = 'newField';
    let i = 1;
    while (d.attire[piece][name] !== undefined) { name = `newField${i++}`; }
    d.attire[piece][name] = '';
  });
  const removePiece = (piece) => patch((d) => { delete d.attire[piece]; });
  const addPiece = () => patch((d) => {
    let name = 'newPiece';
    let i = 1;
    while (d.attire[name] !== undefined) { name = `newPiece${i++}`; }
    d.attire[name] = { label: 'New Attire Piece', designer: 'TBD', madeIn: 'TBD' };
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6" data-testid="event-attire-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#3B2F2F]">Wedding Attire</h3>
          <p className="text-xs text-[#8A7A7A]">Each card is one attire piece. Add key/value details as needed.</p>
        </div>
        <Button onClick={addPiece} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="attire-add-piece">
          <Plus className="w-4 h-4 mr-1" /> Add piece
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {orderedKeys.map((pieceKey) => {
          const piece = attire[pieceKey] || {};
          return (
            <div key={pieceKey} className="border border-[#E5E0D8] rounded-lg p-4" data-testid={`attire-piece-${pieceKey}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 mr-3">
                  <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Piece title</label>
                  <Input
                    value={piece.label || ''}
                    onChange={(e) => updateField(pieceKey, 'label', e.target.value)}
                    data-testid={`attire-label-${pieceKey}`}
                  />
                </div>
                <button
                  onClick={() => removePiece(pieceKey)}
                  className="p-1.5 rounded-md text-red-500 hover:bg-red-50 mt-5"
                  title="Delete piece"
                  data-testid={`attire-remove-${pieceKey}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2">
                {Object.keys(piece).filter((k) => k !== 'label').map((fieldKey) => (
                  <div key={fieldKey} className="grid grid-cols-[110px_1fr_auto] gap-2 items-center">
                    <Input
                      value={fieldKey}
                      onChange={(e) => renameField(pieceKey, fieldKey, e.target.value.replace(/\s+/g, ''))}
                      className="text-xs"
                      data-testid={`attire-fieldkey-${pieceKey}-${fieldKey}`}
                    />
                    <Input
                      value={piece[fieldKey] ?? ''}
                      onChange={(e) => updateField(pieceKey, fieldKey, e.target.value)}
                      data-testid={`attire-fieldval-${pieceKey}-${fieldKey}`}
                    />
                    <button
                      onClick={() => removeField(pieceKey, fieldKey)}
                      className="p-1 rounded text-red-500 hover:bg-red-50"
                      title="Delete field"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={() => addField(pieceKey)}
                className="mt-3 text-xs text-[#3B2F2F] hover:text-[#C8A951] inline-flex items-center gap-1"
                data-testid={`attire-add-field-${pieceKey}`}
              >
                <Plus className="w-3.5 h-3.5" /> Add detail
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Vendors editor ───────────────────────────────────────────────── */
const VendorsEditor = ({ content, patch }) => {
  const vendors = content.vendors || [];
  const update = (i, key, value) => patch((d) => { d.vendors[i][key] = value; });
  const move = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.vendors.length) return;
    [d.vendors[i], d.vendors[j]] = [d.vendors[j], d.vendors[i]];
  });
  const remove = (i) => patch((d) => { d.vendors.splice(i, 1); });
  const add = () => patch((d) => {
    d.vendors = d.vendors || [];
    d.vendors.push({ role: 'New Role', name: 'TBD' });
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6" data-testid="event-vendors-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#3B2F2F]">Wedding Vendors</h3>
          <p className="text-xs text-[#8A7A7A]">Recognised on the public /event page in the order listed here.</p>
        </div>
        <Button onClick={add} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="vendor-add">
          <Plus className="w-4 h-4 mr-1" /> Add vendor
        </Button>
      </div>

      <ul className="space-y-2">
        {vendors.map((v, i) => (
          <li key={i} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-3 items-center p-3 rounded-lg border border-[#E5E0D8]" data-testid={`vendor-row-${i}`}>
            <Input
              value={v.role || ''}
              onChange={(e) => update(i, 'role', e.target.value)}
              placeholder="Role — e.g. Photographer"
              data-testid={`vendor-role-${i}`}
            />
            <Input
              value={v.name || ''}
              onChange={(e) => update(i, 'name', e.target.value)}
              placeholder="Vendor name"
              data-testid={`vendor-name-${i}`}
            />
            <div className="flex items-center gap-1">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move up"
                data-testid={`vendor-up-${i}`}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === vendors.length - 1}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move down"
                data-testid={`vendor-down-${i}`}
              >
                <ArrowDown className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(i)}
                className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                title="Delete"
                data-testid={`vendor-delete-${i}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ── Guest Info editor ────────────────────────────────────────────── */
const GuestInfoEditor = ({ content, patch }) => {
  const gi = content.guestInfo || {};
  const dc = gi.dressCode || {};
  const contact = gi.contact || {};

  const setDressCode = (key, value) => patch((d) => {
    if (!d.guestInfo) d.guestInfo = {};
    if (!d.guestInfo.dressCode) d.guestInfo.dressCode = {};
    d.guestInfo.dressCode[key] = value;
  });
  const setParking = (value) => patch((d) => {
    if (!d.guestInfo) d.guestInfo = {};
    d.guestInfo.parking = value;
  });
  const setContact = (key, value) => patch((d) => {
    if (!d.guestInfo) d.guestInfo = {};
    if (!d.guestInfo.contact) d.guestInfo.contact = {};
    d.guestInfo.contact[key] = value;
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-6" data-testid="event-guest-editor">
      <div>
        <h3 className="font-serif text-xl text-[#3B2F2F]">Guest Information</h3>
        <p className="text-xs text-[#8A7A7A]">These three cards appear on the public /event page under &ldquo;Things to Know.&rdquo;</p>
      </div>

      {/* Dress Code */}
      <div className="border border-[#E5E0D8] rounded-lg p-4">
        <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-3 flex items-center gap-1.5">
          <Crown className="w-3.5 h-3.5 text-[#C8A951]" /> Dress Code
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#8A7A7A] mb-1 block">Title</label>
            <Input
              value={dc.title || ''}
              onChange={(e) => setDressCode('title', e.target.value)}
              placeholder="e.g. Black Tie"
              data-testid="guest-dresscode-title"
            />
          </div>
          <div>
            <label className="text-xs text-[#8A7A7A] mb-1 block">Description</label>
            <Textarea
              rows={3}
              value={dc.description || ''}
              onChange={(e) => setDressCode('description', e.target.value)}
              placeholder="Floor-length gowns for ladies, tuxedos or formal black suits for gentlemen."
              data-testid="guest-dresscode-desc"
            />
          </div>
        </div>
      </div>

      {/* Parking */}
      <div className="border border-[#E5E0D8] rounded-lg p-4">
        <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-3 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-[#C8A951]" /> Parking
        </p>
        <Textarea
          rows={2}
          value={gi.parking || ''}
          onChange={(e) => setParking(e.target.value)}
          placeholder="Complimentary valet and self-parking available on site."
          data-testid="guest-parking"
        />
      </div>

      {/* Contact */}
      <div className="border border-[#E5E0D8] rounded-lg p-4">
        <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-3 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-[#C8A951]" /> Day-of Contact
        </p>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[#8A7A7A] mb-1 block">Name / Role</label>
            <Input
              value={contact.name || ''}
              onChange={(e) => setContact('name', e.target.value)}
              placeholder="Day-of Coordinator"
              data-testid="guest-contact-name"
            />
          </div>
          <div>
            <label className="text-xs text-[#8A7A7A] mb-1 block">Phone</label>
            <Input
              value={contact.phone || ''}
              onChange={(e) => setContact('phone', e.target.value)}
              placeholder="+1 (555) 555-5555"
              data-testid="guest-contact-phone"
            />
          </div>
          <div>
            <label className="text-xs text-[#8A7A7A] mb-1 block">Email</label>
            <Input
              type="email"
              value={contact.email || ''}
              onChange={(e) => setContact('email', e.target.value)}
              placeholder="hello@gracemadeus.com"
              data-testid="guest-contact-email"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── RSVP Deadline editor ─────────────────────────────────────────── */

// Compact, admin-only reproduction of the public /rsvp banner so the
// couple can preview any tier without waiting for real days to pass.
const RSVP_TONE_STYLES = {
  soft:   { text: 'text-white/85',   pill: null,   label: 'Soft (30+ days)' },
  warm:   { text: 'text-[#F5E7B5]',  pill: 'bg-[#C8A951]/15 text-[#F5E7B5] border-[#C8A951]/45',
            chip: (n) => `Just ${n} day${n === 1 ? '' : 's'} left`, label: 'Warm (8–30 days)' },
  urgent: { text: 'text-[#F9DA7C]',  pill: 'bg-[#C8A951]/25 text-[#FFEDA6] border-[#C8A951]/70 shadow-[0_6px_28px_-14px_rgba(200,169,81,0.85)]',
            chip: (n) => `Final week · ${n} day${n === 1 ? '' : 's'} left`, label: 'Urgent (1–7 days)' },
  closed: { text: null, label: 'Closed (past deadline)' },
};

const formatDeadlineISO = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const RsvpTonePreview = ({ tone, rsvp, sampleDaysLeft }) => {
  const dateStr = formatDeadlineISO(rsvp.deadline);
  if (tone === 'closed') {
    return (
      <div className="rounded-xl bg-[#3B2F2F] p-6 flex items-center justify-center" data-testid="rsvp-tone-sample-closed">
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/5 text-white/70 border border-white/15 text-xs">
          <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
          <span>{rsvp.closedNote || 'The RSVP window has closed.'}</span>
        </div>
      </div>
    );
  }
  const cfg = RSVP_TONE_STYLES[tone] || RSVP_TONE_STYLES.soft;
  const noteText = tone === 'soft' ? (rsvp.softNote || 'Please RSVP by') : (rsvp.urgentNote || 'Please RSVP by');
  return (
    <div className="rounded-xl bg-[#3B2F2F] p-6 flex flex-col items-center gap-2" data-testid={`rsvp-tone-sample-${tone}`}>
      <p className={`${cfg.text} text-sm md:text-base`}>
        {noteText} <span className="font-serif italic">{dateStr}</span>.
      </p>
      {cfg.pill && (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest ${cfg.pill}`}>
          {cfg.chip(sampleDaysLeft)}
        </span>
      )}
    </div>
  );
};

const RsvpDeadlineEditor = ({ content, patch }) => {
  const rsvp = content.rsvp || {};
  const setField = (key, value) => patch((d) => {
    if (!d.rsvp) d.rsvp = {};
    d.rsvp[key] = value;
  });

  // "Test the tone" — visual only, never persisted. `auto` lets the real
  // deadline drive the tier; anything else forces a specific preview.
  const [testTone, setTestTone] = useState('auto');

  const deadline = rsvp.deadline || '';
  const daysLeft = (() => {
    if (!deadline) return null;
    const [y, m, d] = deadline.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    // Local-midnight → local-midnight, then Math.round so DST-affected
    // days (23h/25h) don't skew the calendar-day count.
    const dl = new Date(y, m - 1, d);
    const now = new Date();
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((dl - todayMidnight) / 86400000);
  })();
  const autoTone = daysLeft == null
    ? 'unset'
    : daysLeft < 0 ? 'closed'
    : daysLeft <= 7 ? 'urgent'
    : daysLeft <= 30 ? 'warm'
    : 'soft';

  const activeTone = testTone === 'auto' ? autoTone : testTone;
  // Sample days used inside the pill chip when previewing warm/urgent tiers
  const sampleDays = testTone === 'auto'
    ? (daysLeft || 0)
    : testTone === 'urgent' ? 5
    : testTone === 'warm'   ? 21
    : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-6" data-testid="event-rsvp-editor">
      <div>
        <h3 className="font-serif text-xl text-[#3B2F2F]">RSVP Deadline</h3>
        <p className="text-xs text-[#8A7A7A]">
          Sets the "please RSVP by" note that appears on the public /rsvp page.
          The tone shifts from soft cream → warm amber → urgent gold as the date approaches,
          and swaps to the "closed" note once the deadline passes.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Deadline date</label>
          <Input
            type="date"
            value={deadline}
            onChange={(e) => setField('deadline', e.target.value)}
            data-testid="rsvp-deadline-date"
          />
          {daysLeft != null && (
            <p className={`text-xs mt-1 ${daysLeft < 0 ? 'text-red-600' : daysLeft <= 7 ? 'text-[#B09240]' : 'text-[#8A7A7A]'}`}>
              {daysLeft < 0 ? `Deadline passed ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'} ago`
                : daysLeft === 0 ? 'Deadline is today'
                : `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining`}
            </p>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Current live tone</label>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#F8F5EF] text-[#3B2F2F] text-xs uppercase tracking-widest border border-[#E5E0D8]" data-testid="rsvp-live-tone">
            {autoTone}
          </div>
        </div>
      </div>

      {/* Test the tone toggle */}
      <div className="border border-[#E5E0D8] rounded-lg p-4 bg-[#FCFBF7]" data-testid="rsvp-tone-tester">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div>
            <p className="text-sm font-medium text-[#3B2F2F]">Test the tone</p>
            <p className="text-[11px] text-[#8A7A7A]">Preview each tier without changing the deadline. Visual only — nothing is saved.</p>
          </div>
          <div className="inline-flex rounded-full bg-white border border-[#E5E0D8] p-1 text-xs" role="tablist">
            {[
              { id: 'auto',   label: 'Auto' },
              { id: 'soft',   label: 'Soft' },
              { id: 'warm',   label: 'Warm' },
              { id: 'urgent', label: 'Urgent' },
              { id: 'closed', label: 'Closed' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setTestTone(id)}
                className={`px-3 py-1 rounded-full transition-colors ${
                  testTone === id ? 'bg-[#3B2F2F] text-white' : 'text-[#3B2F2F] hover:bg-[#F8F5EF]'
                }`}
                data-testid={`rsvp-tone-toggle-${id}`}
                aria-pressed={testTone === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <RsvpTonePreview tone={activeTone} rsvp={rsvp} sampleDaysLeft={sampleDays} />
        <p className="text-[11px] text-[#8A7A7A] mt-2">
          Previewing on the same dark background used by the public /rsvp section — this is exactly how guests will see it.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Soft note (30+ days out)</label>
          <Input
            value={rsvp.softNote || ''}
            onChange={(e) => setField('softNote', e.target.value)}
            placeholder="Please RSVP by"
            data-testid="rsvp-soft-note"
          />
          <p className="text-[11px] text-[#8A7A7A] mt-1">The date is appended automatically.</p>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Urgent note (0–30 days out)</label>
          <Input
            value={rsvp.urgentNote || ''}
            onChange={(e) => setField('urgentNote', e.target.value)}
            placeholder="The RSVP window is closing — please RSVP by"
            data-testid="rsvp-urgent-note"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Closed note (after deadline)</label>
          <Textarea
            rows={2}
            value={rsvp.closedNote || ''}
            onChange={(e) => setField('closedNote', e.target.value)}
            placeholder="The RSVP window has closed. Please contact us directly if you still need to reply."
            data-testid="rsvp-closed-note"
          />
        </div>
      </div>
    </div>
  );
};

/* ── FAQ editor ───────────────────────────────────────────────────── */
const FaqEditor = ({ content, patch }) => {
  const faqs = content.faqs || [];
  const update = (i, key, value) => patch((d) => {
    if (!d.faqs) d.faqs = [];
    d.faqs[i][key] = value;
  });
  const move = (i, dir) => patch((d) => {
    const j = i + dir;
    if (!d.faqs || j < 0 || j >= d.faqs.length) return;
    [d.faqs[i], d.faqs[j]] = [d.faqs[j], d.faqs[i]];
  });
  const remove = (i) => patch((d) => { d.faqs.splice(i, 1); });
  const add = () => patch((d) => {
    d.faqs = d.faqs || [];
    d.faqs.push({ q: 'New question?', a: 'Answer goes here.' });
  });

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6" data-testid="event-faq-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl text-[#3B2F2F]">Frequently Asked</h3>
          <p className="text-xs text-[#8A7A7A]">Add, edit, delete, or reorder the questions guests see on /event.</p>
        </div>
        <Button onClick={add} className="bg-[#3B2F2F] hover:bg-[#2c2222] text-white" data-testid="faq-add">
          <Plus className="w-4 h-4 mr-1" /> Add question
        </Button>
      </div>

      {faqs.length === 0 && (
        <p className="text-sm text-[#8A7A7A] italic">No FAQ entries yet — add one above.</p>
      )}

      <ul className="space-y-3">
        {faqs.map((row, i) => (
          <li key={i} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start p-3 rounded-lg border border-[#E5E0D8]" data-testid={`faq-row-${i}`}>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Question</label>
              <Input
                value={row.q || ''}
                onChange={(e) => update(i, 'q', e.target.value)}
                placeholder="What time should I arrive?"
                data-testid={`faq-q-${i}`}
              />
              <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 mt-3 block">Answer</label>
              <Textarea
                rows={3}
                value={row.a || ''}
                onChange={(e) => update(i, 'a', e.target.value)}
                placeholder="Please arrive by 2:30 PM."
                data-testid={`faq-a-${i}`}
              />
            </div>
            <div className="flex md:flex-col items-center gap-1 md:pt-6">
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move up" data-testid={`faq-up-${i}`}>
                <ArrowUp className="w-4 h-4" />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === faqs.length - 1}
                className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                title="Move down" data-testid={`faq-down-${i}`}>
                <ArrowDown className="w-4 h-4" />
              </button>
              <button onClick={() => remove(i)}
                className="p-1.5 rounded-md text-red-500 hover:bg-red-50"
                title="Delete" data-testid={`faq-delete-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminEventContentManager;